/**
 * Custom day header component showing date, patient count, and PP count
 */

import { User } from 'lucide-react'
import { formatDateCzech, formatDateForApi } from '@/lib/calendar-utils'
import type { DayStats } from '@/hooks/useCalendarEvents'

interface CalendarDayHeaderProps {
	date: Date
	dayStats: Record<string, DayStats>
}

export function CalendarDayHeader({ date, dayStats }: CalendarDayHeaderProps) {
	const dateKey = formatDateForApi(date)
	const stats = dayStats[dateKey] ?? { patientCount: 0, ppCount: 0 }

	const dayName = formatDateCzech(date, 'EEEE')
	const day = date.getDate()
	const month = date.getMonth() + 1
	const dateStr = month < 10 ? `${day}. ${month}.` : `${day}.${month}.`

	return (
		<div className="flex flex-col items-center py-1">
			{/* Date */}
			<div
				className="font-semibold text-[#3d3428]"
				style={{ fontSize: 'var(--font-size-day-date)' }}
			>
				{dateStr}
			</div>
			{/* Day name */}
			<div
				className="font-medium capitalize text-[#6b5c4c]"
				style={{ fontSize: 'var(--font-size-day-name)' }}
			>
				{dayName}
			</div>

			{/* Stats row */}
			<div
				className="flex items-center gap-4 text-[#6b5c4c] mt-1"
				style={{ fontSize: 'var(--font-size-day-stats)' }}
			>
				{/* Patient count */}
				<span className="inline-flex items-center gap-1">
					<User className="h-4 w-4" />
					<span className="font-semibold">{stats.patientCount}</span>
				</span>

				{/* PP count */}
				<span className="inline-flex items-center gap-1">
					<span className="font-semibold">PP</span>
					<span className="font-semibold">{stats.ppCount}</span>
				</span>
			</div>
		</div>
	)
}

/**
 * Create a header component factory that has access to day stats
 */
export function createDayHeaderComponent(dayStats: Record<string, DayStats>) {
	return function DayHeader({ date }: { date: Date }) {
		return <CalendarDayHeader date={date} dayStats={dayStats} />
	}
}
